/**
 * 웹 OAuth callback — code/state 수신 + start{Google|Apple}Auth 호출.
 *
 * URL: /oauth/{provider}/callback?code=...&state=...
 *
 * 흐름:
 *   1) state 검증 (CSRF)
 *   2) start{Google|Apple}Auth(code, redirectUri) → SocialAuthResult
 *   3) 로그인 또는 즉시 가입 결과로 setAuth + 원래 화면 복귀 (가입 화면 없음)
 *
 * Apple 은 form_post 라 BE web-callback 이 `?code=&state=&name=` 로 relay 한 뒤 여기로 들어온다.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import {
  assertTokens,
  completeSocialSignup,
  startGoogleAuth,
  startAppleAuth,
  type SocialAuthResult,
} from "../../api/social";
import { tokenResponseToAuthUser } from "../../api/auth";
import { useAuthStore } from "../../store/authStore";
import { apiErrorMessage } from "../../lib/errors";
import { track, identifyUser } from "../../lib/tracking";
import { triggerHaptic } from "../../native/haptics";
import { readAndClearState, readAndClearRedirectUri } from "../../lib/socialAuth";
import { useActivityStore } from "../../store/activityStore";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import {
  consumePendingAuthIntent,
  consumeAuthReturnPath,
  discardAuthReturnPath,
} from "../../navigation/pendingAuthIntent";
import { executeNavigateIntent } from "../../navigation/useRequireAuth";
import AgeGateSheet from "../../components/auth/AgeGateSheet";
import BrandWordmark from "../../components/layout/BrandWordmark";
import { Button } from "@astryxdesign/core/Button";
import styles from "./AuthPage.module.css";

interface OAuthCallbackPageProps {
  provider: "google" | "apple";
}

/**
 * OAuth 2.0 표준에서 사용자가 동의 화면에서 거부/취소했을 때 돌아오는 error code 집합.
 * RFC 6749 §4.1.2.1 `access_denied` + provider 별 alias.
 * 이 경우 에러 화면을 띄우지 않고 로그인 화면으로 silent 복귀한다 (사용자 의도된 행동).
 */
const OAUTH_USER_CANCEL_ERRORS = new Set([
  "access_denied",
  "user_cancelled_authorize",
  "user_cancelled_login",
  "user_denied",
]);

export default function OAuthCallbackPage({ provider }: OAuthCallbackPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const activityNavigator = useActivityNavigator();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<"verifying" | "age-gate" | "error">("verifying");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const calledRef = useRef(false);
  /** 처음 보는 계정이라 서버가 내준 가입 대기표 — 연령 확인 뒤 되보낸다 (OI-43). */
  const signupTicketRef = useRef<string | null>(null);

  /** 로그인 화면으로 — auth activity 안이면 entry 만 교체, 아니면 일반 navigate. */
  const backToLogin = useCallback(() => {
    const authActivity = useActivityStore
      .getState()
      .activities.find((a) => a.kind === "auth");
    if (authActivity) {
      activityNavigator.replaceEntry({ url: "/login" });
    } else {
      navigate("/login", { replace: true });
    }
  }, [activityNavigator, navigate]);

  /** 토큰 확보 후 공통 마무리 — 로그인이든 가입이든 여기로 모인다. */
  const finishLogin = useCallback(
    async (result: SocialAuthResult) => {
      const tokens = assertTokens(result);
      const user = tokenResponseToAuthUser(tokens, provider);
      setAuth({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        user,
      });
      identifyUser(user.memberSerialNo);
      // provider 는 파라미터로 — LoginPage.applyTokens 와 같은 계약.
      track("auth_login_success", {
        memberSerialNo: user.memberSerialNo,
        provider,
      });
      await triggerHaptic("light");

      // AuthActivity 가 OAuth 를 열었으면 닫고 대기 인텐트를 실행. 아니면 legacy redirect.
      const authActivity = useActivityStore
        .getState()
        .activities.find((a) => a.kind === "auth");
      if (authActivity) {
        activityNavigator.close(authActivity.id);
        const intent = consumePendingAuthIntent();
        if (intent && intent.type === "navigate") {
          executeNavigateIntent(intent, activityNavigator);
        }
        discardAuthReturnPath();
      } else {
        // OAuth web flow 의 vendor redirect 다음 콜백에서는 활동 스택이 비어있다(메모리 휘발).
        // sessionStorage 에 박아뒀던 source path 로 돌려보내 사용자가 클릭한 화면을 유지한다.
        const returnPath = consumeAuthReturnPath();
        consumePendingAuthIntent();
        navigate(returnPath ?? "/", { replace: true });
      }
    },
    [activityNavigator, navigate, provider, setAuth],
  );

  /**
   * 연령 확인 통과 → 대기표로 가입 마무리 (OI-43).
   *
   * 여기까지 온 사람은 **처음 보는 계정**이다. 기존 회원은 위 인증 한 번으로 이미 끝났다.
   */
  const handleAgeGatePassed = useCallback(
    async (birthYmd: string) => {
      const ticket = signupTicketRef.current;
      if (!ticket) return;
      signupTicketRef.current = null;
      setStatus("verifying");
      try {
        const result = await completeSocialSignup({ signupTicket: ticket, birthYmd });
        if (result.newMember) {
          track("auth_signup_success", { provider });
        }
        await finishLogin(result);
      } catch (ex) {
        // 대기표는 1회용 — 실패하면 소셜 로그인부터 다시다(서버 문구가 그렇게 안내한다).
        track("auth_oauth_error", { provider, reason: "auth_failed" });
        setStatus("error");
        setErrorMessage(apiErrorMessage(ex));
      }
    },
    [finishLogin, provider],
  );

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const errorDescription = params.get("error_description");
    // Apple 최초 인증 시 BE relay 가 붙여주는 표시 이름 (id_token 엔 이름이 없음). 재로그인 시 없음.
    const appleName = params.get("name") ?? undefined;

    (async () => {
      try {
        if (error) {
          // 사용자가 동의 화면에서 취소/거부 — 에러 화면 없이 로그인 화면으로 silent 복귀.
          if (OAUTH_USER_CANCEL_ERRORS.has(error)) {
            const authActivity = useActivityStore
              .getState()
              .activities.find((a) => a.kind === "auth");
            if (authActivity) {
              activityNavigator.replaceEntry({ url: "/login" });
            } else {
              navigate("/login", { replace: true });
            }
            return;
          }
          throw new Error(errorDescription || error);
        }
        if (!code || !state) {
          throw new Error(i18n.t("auth.missingParams"));
        }

        // state 검증 (CSRF)
        const providerKey = provider === "apple" ? "APPLE" : "GOOGLE";
        const expectedState = readAndClearState(providerKey);
        if (!expectedState || expectedState !== state) {
          throw new Error(i18n.t("auth.stateMismatch"));
        }

        const redirectUri = readAndClearRedirectUri(providerKey);
        if (!redirectUri) {
          throw new Error(i18n.t("auth.missingRedirect"));
        }

        const result: SocialAuthResult =
          provider === "apple"
            ? await startAppleAuth({ code, redirectUri, name: appleName })
            : await startGoogleAuth({ code, redirectUri });

        // 처음 보는 계정 — 아직 회원이 만들어지지 않았다. 연령을 확인해야 가입이 끝난다.
        if (result.kind === "NEED_AGE") {
          signupTicketRef.current = result.signupTicket;
          setStatus("age-gate");
          return;
        }
        await finishLogin(result);
      } catch (ex) {
        // 웹 redirect 복귀 단계 실패 — 콘솔 미등록·redirect URI 불일치가 주로 여기서 난다.
        // ⚠ 서버 문구는 안 보낸다(자유 텍스트 = PII 위험). LoginPage.trackOAuthError 와 같은 라벨.
        track("auth_oauth_error", { provider, reason: "auth_failed" });
        setStatus("error");
        setErrorMessage(apiErrorMessage(ex));
      }
    })();
  }, [navigate, provider, params, activityNavigator, finishLogin]);

  if (status === "verifying") {
    return (
      <div className={styles.oauthCallback}>
        <BrandWordmark height={35} />
        <p style={{ color: "var(--text-secondary)", fontSize: 14, margin: 0 }}>
          {t("auth.callbackProgress", { provider: provider === "apple" ? "Apple" : "Google" })}
        </p>
      </div>
    );
  }

  // 가입 직전 연령 확인 — 닫으면 대기표를 버리고 로그인 화면으로 돌아간다(회원은 안 만들어진다).
  if (status === "age-gate") {
    return (
      <div className={styles.oauthCallback}>
        <BrandWordmark height={35} />
        <AgeGateSheet
          open
          onClose={() => {
            signupTicketRef.current = null;
            backToLogin();
          }}
          onPassed={(birthYmd) => void handleAgeGatePassed(birthYmd)}
        />
      </div>
    );
  }

  return (
    <div className={styles.oauthCallback}>
      <BrandWordmark height={35} />
      <div className={styles.serverError}>{errorMessage ?? t("error.unexpected")}</div>
      <Button
        variant="primary"
        size="lg"
        label={t("auth.backToLogin")}
        clickAction={backToLogin}
      />
    </div>
  );
}
